import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

import "./Trans.css";

const formatCurrency = value => {
  const amount = Number(value);

  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number.isFinite(amount) ? amount : 0);
};

const formatDate = value => {
  if (!value) return "-";

  const normalizedValue = /^\d{2}-\d{2}-\d{4}/.test(value)
    ? value.replace(/^(\d{2})-(\d{2})-(\d{4})/, "$3-$2-$1")
    : value;
  const date = new Date(normalizedValue);

  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const getMerchantName = row => row?.seller_details?.name || row?.merchant_name || "-";

const getInitials = name => {
  if (!name || name === "-") return "?";

  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word.charAt(0))
    .join("")
    .toUpperCase();
};

const getStatusTone = (status, name = "") => {
  const normalizedStatus = String(status || "");
  const normalizedName = name.toLowerCase();

  if (["5"].includes(normalizedStatus) || /selesai|lunas|settlement|success/.test(normalizedName)) {
    return "success";
  }

  if (["98", "99"].includes(normalizedStatus) || /batal|gagal|refund|expire|deny/.test(normalizedName)) {
    return "danger";
  }

  if (/menunggu|pending|proses/.test(normalizedName)) return "warning";

  return "info";
};

const initialFilters = {
  invoice: "",
  merchant: "",
  status: "",
};

const Trans = props => {
  const [data, setData] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchTransactions = async (
    page = currentPage,
    size = perPage,
    queryFilters = filters
  ) => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        global.variable.LINK_API + `report/list?page=${page}&per_page=${size}`,
        {
          invoice_id: queryFilters.invoice.trim(),
          merchant: queryFilters.merchant.trim(),
          status: queryFilters.status ? [queryFilters.status] : [],
        },
        { ...global.variable.HEADERS_BEARER, cancelKey: "transaction-list" }
      );

      setData(response.data?.data || []);
      setTotalRows(response.data?.total || 0);
    } catch (requestError) {
      setData([]);
      setTotalRows(0);
      setError(
        requestError?.response?.data?.message ||
          "Data transaksi belum dapat dimuat. Silakan coba kembali."
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchStatuses = async () => {
    try {
      const response = await axios.get(
        global.variable.LINK_API + "param/status_transaction",
        { ...global.variable.HEADERS_BEARER, cancelKey: "transaction-status-options" }
      );
      setStatusOptions(Array.isArray(response.data) ? response.data : []);
    } catch (requestError) {
      setStatusOptions([]);
    }
  };

  useEffect(() => {
    fetchTransactions(1, perPage, filters);
    fetchStatuses();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredData = useMemo(() => {
    const invoiceSearch = filters.invoice.trim().toLowerCase();
    const merchantSearch = filters.merchant.trim().toLowerCase();

    return data.filter(row => {
      const matchesInvoice =
        !invoiceSearch || String(row?.invoice_id || "").toLowerCase().includes(invoiceSearch);
      const matchesMerchant =
        !merchantSearch || getMerchantName(row).toLowerCase().includes(merchantSearch);
      const matchesStatus = !filters.status || String(row?.status) === String(filters.status);

      return matchesInvoice && matchesMerchant && matchesStatus;
    });
  }, [data, filters]);

  const availableStatuses = useMemo(() => {
    const statuses = statusOptions.slice();

    data.forEach(row => {
      const value = row?.status;
      const text = row?.invoice_status_name || row?.transaction_status;
      const exists = statuses.some(option => String(option.value) === String(value));

      if (value !== undefined && value !== null && text && !exists) {
        statuses.push({ value, text });
      }
    });

    return statuses;
  }, [data, statusOptions]);

  const visibleAmount = filteredData.reduce(
    (total, transaction) => total + (Number(transaction?.gross_amount) || 0),
    0
  );
  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const lastPage = Math.max(1, Math.ceil(totalRows / perPage));
  const firstRowNumber = totalRows ? (currentPage - 1) * perPage + 1 : 0;
  const lastRowNumber = Math.min(currentPage * perPage, totalRows);

  const updateFilter = event => {
    const { name, value } = event.target;
    setFilters(currentFilters => ({ ...currentFilters, [name]: value }));
  };

  const applyFilters = event => {
    event.preventDefault();
    setCurrentPage(1);
    fetchTransactions(1, perPage, filters);
  };

  const resetFilters = () => {
    setFilters(initialFilters);
    setCurrentPage(1);
    fetchTransactions(1, perPage, initialFilters);
  };

  const changePage = page => {
    if (page < 1 || page > lastPage || page === currentPage) return;

    setCurrentPage(page);
    fetchTransactions(page, perPage);
  };

  const changePerPage = event => {
    const size = Number(event.target.value);
    setPerPage(size);
    setCurrentPage(1);
    fetchTransactions(1, size);
  };

  const openDetail = invoiceId => {
    props.history.push("/transactiondetail/" + encodeURIComponent(invoiceId));
  };

  return (
    <section className="transaction-page">
      <div className="transaction-summary">
        <article className="transaction-summary__card">
          <span className="transaction-summary__icon transaction-summary__icon--purple">
            <i className="fas fa-file-invoice" aria-hidden="true" />
          </span>
          <div>
            <span>Total transaksi</span>
            <strong>{totalRows.toLocaleString("id-ID")}</strong>
          </div>
        </article>
        <article className="transaction-summary__card">
          <span className="transaction-summary__icon transaction-summary__icon--blue">
            <i className="fas fa-list" aria-hidden="true" />
          </span>
          <div>
            <span>Hasil ditampilkan</span>
            <strong>{filteredData.length.toLocaleString("id-ID")}</strong>
          </div>
        </article>
        <article className="transaction-summary__card">
          <span className="transaction-summary__icon transaction-summary__icon--green">
            <i className="fas fa-wallet" aria-hidden="true" />
          </span>
          <div>
            <span>Nominal ditampilkan</span>
            <strong>{formatCurrency(visibleAmount)}</strong>
          </div>
        </article>
      </div>

      <form className="transaction-filter" onSubmit={applyFilters}>
        <div className="transaction-filter__title">
          <div>
            <span className="transaction-filter__icon">
              <i className="fas fa-sliders-h" aria-hidden="true" />
            </span>
            <div>
              <h2>Filter transaksi</h2>
              <p>Persempit hasil berdasarkan data invoice.</p>
            </div>
          </div>
          {activeFilterCount > 0 && (
            <span className="transaction-filter__count">{activeFilterCount} filter aktif</span>
          )}
        </div>

        <div className="transaction-filter__fields">
          <label>
            <span>Nomor invoice</span>
            <div className="transaction-filter__control">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="search"
                name="invoice"
                value={filters.invoice}
                onChange={updateFilter}
                placeholder="Contoh: INV-2025..."
              />
            </div>
          </label>

          <label>
            <span>Merchant</span>
            <div className="transaction-filter__control">
              <i className="fas fa-store" aria-hidden="true" />
              <input
                type="search"
                name="merchant"
                value={filters.merchant}
                onChange={updateFilter}
                placeholder="Cari nama merchant"
              />
            </div>
          </label>

          <label>
            <span>Status transaksi</span>
            <div className="transaction-filter__control transaction-filter__control--select">
              <i className="fas fa-check-circle" aria-hidden="true" />
              <select name="status" value={filters.status} onChange={updateFilter}>
                <option value="">Semua status</option>
                {availableStatuses.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.text}
                  </option>
                ))}
              </select>
            </div>
          </label>

          <div className="transaction-filter__actions">
            <button type="button" className="transaction-filter__reset" onClick={resetFilters}>
              Reset
            </button>
            <button type="submit" className="transaction-filter__submit" disabled={loading}>
              <i className="fas fa-search" aria-hidden="true" />
              Terapkan
            </button>
          </div>
        </div>
      </form>

      <div className="transaction-table-card">
        <div className="transaction-table-card__heading">
          <div>
            <h2>Transaksi terbaru</h2>
            <p>Urutan berdasarkan transaksi terbaru.</p>
          </div>
          <div className="transaction-table-card__actions">
            <button type="button" className="transaction-page__refresh" onClick={() => fetchTransactions(currentPage, perPage)} disabled={loading}><i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} aria-hidden="true" />Refresh</button>
            <label className="transaction-table-card__page-size">Tampilkan<select value={perPage} onChange={changePerPage}><option value={10}>10</option><option value={25}>25</option><option value={50}>50</option></select>baris</label>
          </div>
        </div>

        {error ? (
          <div className="transaction-state transaction-state--error">
            <span><i className="fas fa-exclamation-triangle" aria-hidden="true" /></span>
            <h3>Gagal memuat transaksi</h3>
            <p>{error}</p>
            <button type="button" onClick={() => fetchTransactions(currentPage, perPage)}>
              Coba lagi
            </button>
          </div>
        ) : loading ? (
          <div className="transaction-state">
            <span className="transaction-state__loader" />
            <h3>Memuat transaksi</h3>
            <p>Mohon tunggu sebentar.</p>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="transaction-state">
            <span><i className="fas fa-search" aria-hidden="true" /></span>
            <h3>Transaksi tidak ditemukan</h3>
            <p>Coba ubah kata kunci atau reset filter yang digunakan.</p>
            {activeFilterCount > 0 && (
              <button type="button" onClick={resetFilters}>Reset filter</button>
            )}
          </div>
        ) : (
          <div className="transaction-table-wrap">
            <table className="transaction-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Merchant</th>
                  <th>Status</th>
                  <th>Nominal</th>
                  <th>Tanggal</th>
                  <th><span className="sr-only">Aksi</span></th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map(row => {
                  const merchantName = getMerchantName(row);
                  const statusName = row?.invoice_status_name || row?.transaction_status || "Status tidak tersedia";
                  const statusTone = getStatusTone(row?.status, statusName);

                  return (
                    <tr key={row?.invoice_id || row?.id}>
                      <td data-label="Invoice">
                        <button
                          type="button"
                          className="transaction-table__invoice"
                          onClick={() => openDetail(row.invoice_id)}
                        >
                          {row?.invoice_id || "-"}
                        </button>
                        <small>{row?.order_id || "Nomor order tidak tersedia"}</small>
                      </td>
                      <td data-label="Merchant">
                        <div className="transaction-table__merchant">
                          <span>{getInitials(merchantName)}</span>
                          <div>
                            <strong>{merchantName}</strong>
                            <small>Merchant</small>
                          </div>
                        </div>
                      </td>
                      <td data-label="Status">
                        <span className={`transaction-status transaction-status--${statusTone}`}>
                          <i aria-hidden="true" />
                          {statusName}
                        </span>
                      </td>
                      <td data-label="Nominal" className="transaction-table__amount">
                        {formatCurrency(row?.gross_amount)}
                      </td>
                      <td data-label="Tanggal" className="transaction-table__date">
                        {formatDate(row?.transaction_date || row?.created_at)}
                      </td>
                      <td data-label="Aksi" className="transaction-table__action">
                        <button
                          type="button"
                          onClick={() => openDetail(row.invoice_id)}
                          aria-label={`Lihat detail ${row?.invoice_id || "invoice"}`}
                        >
                          Detail
                          <i className="fas fa-arrow-right" aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!error && !loading && totalRows > 0 && (
          <footer className="transaction-pagination">
            <p>
              Menampilkan <strong>{firstRowNumber}-{lastRowNumber}</strong> dari{" "}
              <strong>{totalRows.toLocaleString("id-ID")}</strong> transaksi
            </p>
            <div>
              <button
                type="button"
                onClick={() => changePage(currentPage - 1)}
                disabled={currentPage === 1}
                aria-label="Halaman sebelumnya"
              >
                <i className="fas fa-chevron-left" aria-hidden="true" />
              </button>
              <span>Halaman {currentPage} dari {lastPage}</span>
              <button
                type="button"
                onClick={() => changePage(currentPage + 1)}
                disabled={currentPage === lastPage}
                aria-label="Halaman berikutnya"
              >
                <i className="fas fa-chevron-right" aria-hidden="true" />
              </button>
            </div>
          </footer>
        )}
      </div>
    </section>
  );
};

export default Trans;
